import { useReducer } from 'react';
import type {
  IDFP,
  IGetAdsRequestFilter,
  IGetAdsRequestRange,
  ISearchPageAds,
  ISearchPagePaging,
  ISearchPageSeoData,
} from 'api/types/searchPageApiTypes';
import { GTMEvent } from 'services/gtmService';
import {
  mapFiltersRequest,
  mapRangesRequest,
} from 'components/DealerPage/mappers/filters';
import {
  ACTION_TYPE,
  AdsType,
  ErrorType,
  mapMakeModelFilters,
  SeoDataType,
  useAdsReducer,
  UseAdsType,
} from 'components/DealerPage/hooks/useAds';
import { showroomRepository } from 'repositories/ShowroomRepository';
import { isOk } from 'domains/Result';
import { SearchPageBreadcrumbs } from 'api/types/searchPageApiTypes';

export type MakeModelItem = {
  id: string;
  makeValue: string;
  modelValue: string[];
  isEditable: boolean;
};

export type MakeModelType = MakeModelItem[];

const useDealerShowroomAds = (
  dealerId: string,
  defaultAds: ISearchPageAds[] = [],
  defaultSeoData: ISearchPageSeoData | undefined,
  defaultDFP: IDFP,
  defaultGTM: GTMEvent,
  setPaging: (paging: ISearchPagePaging) => void,
): UseAdsType => {
  const [state, dispatch] = useReducer(useAdsReducer, {
    isLoading: false,
    ads: defaultAds,
    spotlights: [],
    error: null,
    seoData: defaultSeoData,
    dfp: defaultDFP,
    gtm: defaultGTM,
  });

  const setLoading = (loading: boolean) =>
    dispatch({
      type: ACTION_TYPE.SET_LOADING,
      payload: loading,
    });

  const setAds = (
    ads: AdsType | undefined,
    spotlights: AdsType | undefined,
    seoData: SeoDataType | undefined,
    dfp: IDFP,
    gtm: GTMEvent,
  ) =>
    dispatch({
      type: ACTION_TYPE.SET_ADS,
      payload: {
        ads: ads as AdsType,
        spotlights: spotlights as AdsType,
        seoData: seoData as SeoDataType,
        dfp,
        gtm,
      },
    });

  const setError = (error: ErrorType) =>
    dispatch({
      type: ACTION_TYPE.SET_ERROR,
      payload: error,
    });

  const fetchAds = async (
    filters: IGetAdsRequestFilter[],
    ranges: IGetAdsRequestRange[],
    makeModel: MakeModelType,
    page: number,
    pageSize: number,
    section: string,
    search: string,
    sort: string,
    userId: string | string[] | undefined,
    onStart: () => void,
    onSuccess: (breadcrumbs: SearchPageBreadcrumbs[]) => void,
  ) => {
    setLoading(true);
    onStart();
    const editedFilters = [...filters];
    if (userId) {
      Array.isArray(userId)
        ? editedFilters.push({
            name: 'userId',
            values: userId,
          })
        : editedFilters.push({
            name: 'userId',
            values: [userId],
          });
    }

    try {
      const response = await showroomRepository.getDealerShowroom(dealerId, {
        filters: mapFiltersRequest(editedFilters),
        ...(ranges.length && { ranges: mapRangesRequest(ranges) }),
        ...(makeModel.length && {
          makeModelFilters: mapMakeModelFilters(makeModel),
        }),
        paging: {
          pageSize: 10,
          from: page * pageSize,
        },
        sections: [section],
        terms: search,
        sort,
      });

      if (isOk(response)) {
        const { ads, paging } = response.ok;
        setAds(
          ads,
          [],
          {
            internalLinks: [],
          },
          {
            vertical: [],
            section: [],
            words: [],
            area: [],
            user_id: [],
          },
          {},
        );
        setPaging(paging);
        onSuccess([]);
      }
    } catch (err) {
      setError(err);
    }
  };

  const combineAds = (ads?: AdsType) => [...(ads || [])];

  return {
    ...state,
    fetchAds,
    setLoading,
    setAds,
    setError,
    combineAds,
  };
};

export { useDealerShowroomAds };
